import _asyncToGenerator from "@babel/runtime/helpers/asyncToGenerator";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _regeneratorRuntime from "@babel/runtime/regenerator";
import React from 'react';
import Loadable from 'react-loadable';
import { getBooleanFF } from '@atlaskit/platform-feature-flags';
export var isCardWithData = function isCardWithData(props) {
  return !!props.data;
};
export var isSpecialEvent = function isSpecialEvent(evt) {
  return evt.isDefaultPrevented() && (isIframe() || isSpecialKey(evt) || isSpecialClick(evt));
};
export var isIframe = function isIframe() {
  return window.parent !== parent;
};

/**
 * Meta key = cmd on mac, windows key on windows
 * Ctrl key on mac by default triggers a right click instead of left click
 * Ctrl key on Windows has the same behaviour of cmd key of mac (open in new tab)
 *
 * `isSpecialKey` on a mouse event on mac with default behaviour should be equivalent to opening in new tab
 * On Windows it will be equivalent to opening a new tab, unless its the Window key that is held
 * in which case typically only a standard clickthrough will occur, this is likely a small portion of events
 */
export var isSpecialKey = function isSpecialKey(event) {
  return event.metaKey || event.ctrlKey;
};
export var isSpecialClick = function isSpecialClick(event) {
  return event.button === 1;
};
export var getIconForFileType = function getIconForFileType(fileMimeType) {
  if (!fileMimeType) {
    return;
  }
  var icon = typeToIcon[fileMimeType.toLowerCase()];
  if (!icon) {
    return;
  }
  var _icon = _slicedToArray(icon, 2),
    label = _icon[0],
    importCb = _icon[1];
  if (!importCb) {
    return;
  }
  var Icon = Loadable({
    loader: function loader() {
      return importCb().then(function (module) {
        return module.default;
      });
    },
    loading: function loading() {
      return null;
    }
  }); // because we're using dynamic loading here, TS will not be able to infer the type

  return /*#__PURE__*/React.createElement(Icon, {
    label: label,
    testId: "document-file-format-icon"
  });
};
export var getLabelForFileType = function getLabelForFileType(fileMimeType) {
  var icon = typeToIcon[fileMimeType.toLowerCase()];
  if (!icon) {
    return;
  }
  var _icon2 = _slicedToArray(icon, 1),
    label = _icon2[0];
  return label;
};
var typeToIcon = {
  'text/plain': ['Document', function () {
    return import('@atlaskit/icon-file-type/glyph/document/16');
  }],
  'application/vnd.oasis.opendocument.text': ['Document', function () {
    return import('@atlaskit/icon-file-type/glyph/document/16');
  }],
  'application/vnd.apple.pages': ['Document', function () {
    return import('@atlaskit/icon-file-type/glyph/document/16');
  }],
  'application/vnd.google-apps.document': ['Google Doc', function () {
    return import('@atlaskit/icon-file-type/glyph/google-doc/16');
  }],
  'application/msword': ['Word document', function () {
    return import('@atlaskit/icon-file-type/glyph/word-document/16');
  }],
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['Word document', function () {
    return import('@atlaskit/icon-file-type/glyph/word-document/16');
  }],
  'application/pdf': ['PDF document', function () {
    return import('@atlaskit/icon-file-type/glyph/pdf-document/16');
  }],
  'application/vnd.oasis.opendocument.spreadsheet': ['Spreadsheet', function () {
    return import('@atlaskit/icon-file-type/glyph/spreadsheet/16');
  }],
  'application/vnd.apple.numbers': ['Spreadsheet', function () {
    return import('@atlaskit/icon-file-type/glyph/spreadsheet/16');
  }],
  'application/vnd.google-apps.spreadsheet': ['Google Sheet', function () {
    return import('@atlaskit/icon-file-type/glyph/google-sheet/16');
  }],
  'application/vnd.ms-excel': ['Excel spreadsheet', function () {
    return import('@atlaskit/icon-file-type/glyph/excel-spreadsheet/16');
  }],
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': ['Excel spreadsheet', function () {
    return import('@atlaskit/icon-file-type/glyph/excel-spreadsheet/16');
  }],
  'application/vnd.oasis.opendocument.presentation': ['Presentation', function () {
    return import('@atlaskit/icon-file-type/glyph/presentation/16');
  }],
  'application/vnd.apple.keynote': ['Presentation', function () {
    return import('@atlaskit/icon-file-type/glyph/presentation/16');
  }],
  'application/vnd.google-apps.presentation': ['Google Slide', function () {
    return import('@atlaskit/icon-file-type/glyph/google-slide/16');
  }],
  'application/vnd.ms-powerpoint': ['PowerPoint presentation', function () {
    return import('@atlaskit/icon-file-type/glyph/powerpoint-presentation/16');
  }],
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': ['PowerPoint presentation', function () {
    return import('@atlaskit/icon-file-type/glyph/powerpoint-presentation/16');
  }],
  'application/vnd.google-apps.form': ['Google Form', function () {
    return import('@atlaskit/icon-file-type/glyph/google-form/16');
  }],
  'image/png': ['Image', function () {
    return import('@atlaskit/icon-file-type/glyph/image/16');
  }],
  'image/jpeg': ['Image', function () {
    return import('@atlaskit/icon-file-type/glyph/image/16');
  }],
  'image/bmp': ['Image', function () {
    return import('@atlaskit/icon-file-type/glyph/image/16');
  }],
  'image/webp': ['Image', function () {
    return import('@atlaskit/icon-file-type/glyph/image/16');
  }],
  'image/svg+xml': ['Image', function () {
    return import('@atlaskit/icon-file-type/glyph/image/16');
  }],
  'image/gif': ['GIF', function () {
    return import('@atlaskit/icon-file-type/glyph/gif/16');
  }],
  'audio/midi': ['Audio', function () {
    return import('@atlaskit/icon-file-type/glyph/audio/16');
  }],
  'audio/mpeg': ['Audio', function () {
    return import('@atlaskit/icon-file-type/glyph/audio/16');
  }],
  'audio/webm': ['Audio', function () {
    return import('@atlaskit/icon-file-type/glyph/audio/16');
  }],
  'audio/ogg': ['Audio', function () {
    return import('@atlaskit/icon-file-type/glyph/audio/16');
  }],
  'audio/wav': ['Audio', function () {
    return import('@atlaskit/icon-file-type/glyph/audio/16');
  }],
  'video/mp4': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/quicktime': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/mov': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/webm': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/ogg': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/x-ms-wmv': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'video/x-msvideo': ['Video', function () {
    return import('@atlaskit/icon-file-type/glyph/video/16');
  }],
  'application/zip': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/x-tar': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/x-gtar': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/x-7z-compressed': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/x-apple-diskimage': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/vnd.rar': ['Archive', function () {
    return import('@atlaskit/icon-file-type/glyph/archive/16');
  }],
  'application/dmg': ['Executable', function () {
    return import('@atlaskit/icon-file-type/glyph/executable/16');
  }],
  'text/css': ['Source Code', function () {
    return import('@atlaskit/icon-file-type/glyph/source-code/16');
  }],
  'text/html': ['Source Code', function () {
    return import('@atlaskit/icon-file-type/glyph/source-code/16');
  }],
  'application/javascript': ['Source Code', function () {
    return import('@atlaskit/icon-file-type/glyph/source-code/16');
  }],
  'application/octet-stream': ['Binary file', function () {
    return import('@atlaskit/icon-file-type/glyph/generic/16');
  }],
  'application/invision.prototype': ['Prototype', function () {
    return import('@atlaskit/icon-file-type/glyph/generic/16');
  }],
  // TODO: Figure a way to detect those
  'application/sketch': ['Sketch', function () {
    return import('@atlaskit/icon-file-type/glyph/sketch/16');
  }],
  folder: ['Folder', function () {
    return import('@atlaskit/icon-file-type/glyph/folder/16');
  }]
};
export var getIframeSandboxAttribute = function getIframeSandboxAttribute(isTrusted) {
  if (isTrusted) {
    return undefined;
  }
  var sandboxPermissions = 'allow-downloads allow-forms allow-popups allow-popups-to-escape-sandbox allow-presentation allow-same-origin allow-scripts';
  var allowStorageAccess = 'allow-storage-access-by-user-activation';
  if (getBooleanFF('platform.linking-platform.smart-card.iframes-allow-storage-access-by-user-activation')) {
    return "".concat(sandboxPermissions, " ").concat(allowStorageAccess);
  }
  return sandboxPermissions;
};
export var handleOnClick = function handleOnClick(handler) {
  return function (e) {
    e.preventDefault();
    e.stopPropagation();
    handler();
  };
};
export var isIntersectionObserverSupported = function isIntersectionObserverSupported() {
  return typeof IntersectionObserver !== 'undefined';
};
export var sleep = function sleep(ms) {
  return new Promise(function (resolve) {
    return setTimeout(resolve, ms);
  });
};
export var importWithRetry = /*#__PURE__*/function () {
  var _ref = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee(importFn) {
    var retries,
      interval,
      _args = arguments;
    return _regeneratorRuntime.wrap(function _callee$(_context) {
      while (1) switch (_context.prev = _context.next) {
        case 0:
          retries = _args.length > 1 && _args[1] !== undefined ? _args[1] : 2;
          interval = _args.length > 2 && _args[2] !== undefined ? _args[2] : 500;
          _context.prev = 2;
          _context.next = 5;
          return importFn();
        case 5:
          return _context.abrupt("return", _context.sent);
        case 8:
          _context.prev = 8;
          _context.t0 = _context["catch"](2);
          if (!(_context.t0.name === 'ChunkLoadError' && retries > 0)) {
            _context.next = 16;
            break;
          }
          _context.next = 13;
          return sleep(interval);
        case 13:
          return _context.abrupt("return", importWithRetry(importFn, retries - 1, interval));
        case 16:
          throw _context.t0;
        case 17:
        case "end":
          return _context.stop();
      }
    }, _callee, null, [[2, 8]]);
  }));
  return function importWithRetry(_x) {
    return _ref.apply(this, arguments);
  };
}();
export var downloadUrl = /*#__PURE__*/function () {
  var _ref2 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee2(url) {
    var isIE11, isSafari, iframeName, link, iframe;
    return _regeneratorRuntime.wrap(function _callee2$(_context2) {
      while (1) switch (_context2.prev = _context2.next) {
        case 0:
          if (url) {
            _context2.next = 2;
            break;
          }
          return _context2.abrupt("return");
        case 2:
          isIE11 = !!window.MSInputMethodContext && !!document.documentMode;
          isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
          iframeName = 'media-download-iframe';
          link = document.createElement('a');
          iframe = document.getElementById(iframeName);
          if (!iframe) {
            iframe = document.createElement('iframe');
            iframe.style.display = 'none';
            iframe.id = iframeName;
            iframe.name = iframeName;
            document.body.appendChild(iframe);
          }
          link.href = url;
          link.download = url;
          link.target = isIE11 || isSafari ? '_blank' : iframeName;
          document.body.appendChild(link);
          link.click();
          document.body.removeChild(link);
        case 14:
        case "end":
          return _context2.stop();
      }
    }, _callee2);
  }));
  return function downloadUrl(_x2) {
    return _ref2.apply(this, arguments);
  };
}();
export var openUrl = /*#__PURE__*/function () {
  var _ref3 = _asyncToGenerator( /*#__PURE__*/_regeneratorRuntime.mark(function _callee3(url) {
    return _regeneratorRuntime.wrap(function _callee3$(_context3) {
      while (1) switch (_context3.prev = _context3.next) {
        case 0:
          if (url) {
            _context3.next = 2;
            break;
          }
          return _context3.abrupt("return");
        case 2:
          window.open(url, '_blank', 'noopener=yes');
        case 3:
        case "end":
          return _context3.stop();
      }
    }, _callee3);
  }));
  return function openUrl(_x3) {
    return _ref3.apply(this, arguments);
  };
}();